import i18n from '@/vendors/i18n'
import blocks from '@/configurations/procedures/blocks'
import { get as _get, clone as _clone, uniqWith as _uniqWith, isEqual as _isEqual } from 'lodash'
import { operators, guid, isClassInstance } from '@/utils/utils'

export default class Block {
  constructor ({ name = null, model = null, attributes = {}, mandatory = [], options = {}, conditions = [] } = {}) {
    this.id = name + guid(2)
    this.name = name
    this.model = model
    this.attributes = attributes
    this.mandatory = mandatory
    this.options = options
    this.conditions = conditions
  }

  get reference () {
    return _clone(blocks.find(block => block.name === this.name))
  }

  get component_path () {
    return this.reference?.component_path
  }

  get options () {
    return this._options
  }

  set options (options) {
    this._options = { ..._get(this.reference, 'static_options', {}), ...options }
  }

  get conditions () {
    return this._conditions
  }

  set conditions (conditions) {
    this._conditions = _uniqWith([..._get(this.reference, 'conditions', []), ...conditions], _isEqual)
  }

  get mandatory () {
    return this._mandatory
  }

  set mandatory (mandatory) {
    _get(this.reference, 'static_mandatory', []).forEach(sm => {
      if (!mandatory.includes(sm)) mandatory.push(sm)
    })

    this._mandatory = mandatory
  }

  get default_value () {
    return _get(this.reference, 'default_value', null)
  }

  get is_required () {
    return Array.isArray(this.mandatory) && ['required', 'sameAs:true', 'productRequired', 'minAmount'].some(validator => this.mandatory.some(m => m.includes(validator)))
  }

  isAvailable (data) {
    if (this.name === 'transaction_payment_methods') return _get(data, 'actions_data.transaction.amount', 0) > 0
    return this.isVisible(data)
  }

  isVisible (data) {
    if (!this.conditions.length || !this.options.has_conditions) return true

    const conditions = this.conditions
      .map(condition => {
        if (!condition.model) return { operator: condition.logic_operator, value: true }

        let value = i18n.translate(condition.value)
        let field = _get(data, condition.model)

        if (typeof field === 'string') field = field.replace(/\s+/g, '').toLowerCase()
        if (isClassInstance(field) && 'to_condition' in field) field = field.to_condition

        let target, comparisonResult

        if (['date', 'today'].includes(condition.value_type)) {
          value = new Date(condition.value_type === 'today' ? Date.now() : value).setHours(0, 0, 0, 0)
          field = field ? new Date(field).setHours(0, 0, 0, 0) : null
        }

        if (condition.value_type === 'empty') {
          comparisonResult = condition.comparison_operator === 'eq' ? operators['empty'](field) : operators['nempty'](field)
        } else if (condition.value_type === 'checked') {
          if (field === null) field = false
          comparisonResult = condition.comparison_operator === 'eq' ? operators['checked'](field) : operators['unchecked'](field)
        } else if (condition.value_type === 'unchecked') {
          if (field === null) field = false
          comparisonResult = condition.comparison_operator === 'eq' ? operators['unchecked'](field) : operators['checked'](field)
        } else {
          target = operators[condition.value_type] ? operators[condition.value_type](value) : value
          comparisonResult = operators[condition.comparison_operator](field, target)
        }

        return { operator: condition.logic_operator, value: comparisonResult }
      })

    return conditions.length === 1 ? conditions[0].value : conditions.reduce((a, b) => operators[b.operator](a.value, b.value))
  }
}
